import React, { useState, useEffect, useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet} from "react-router-dom";
import { useSelector, useDispatch, ReactReduxContext } from "react-redux";

// CSS FILES
import "./App.css";
import "../src/css/main.css";

// FILE IMPORTS
import Login from "./screens/Auth/Login.jsx";
import { ForgotPassword, Register, ResetPassword } from "./screens/Auth/PasswordFlows.jsx";
import NotFound from "./components/layout/NotFound";
import Loader from "./components/layout/Loader";
import CreateAdmin from "./screens/Admin/InviteUser.jsx";
// import Dashboard from "./components/main/Dashboard/Dashboard.jsx";
// import AgentDetails from "./components/main/Dashboard/AgentDetail.jsx";
import ToastContainer from "./toaster/toaster.jsx";
import AdminList from "./screens/Admin/Team.jsx";
// import PromotionPricing from "./components/main/PromotionPricing/PromotionPricing.jsx";
import AppVideo from "./screens/Growth/AppVideo.jsx";
import Profile from "./screens/Admin/Profile.jsx";
// import PromotionPricingEdit from "./components/main/PromotionPricing/PromotionPricingEdit.jsx";
// import ApprovedAstrologer from "./components/main/ApprovedAstrologer/ApprovedAstrologer.jsx";
import SupportTicket from "./screens/Operations/SupportTicket.jsx";
import NameCorrection from "./screens/Operations/NameCorrection.jsx";
import Report from "./screens/Operations/Reports.jsx";
import CouponPromotions from "./screens/Growth/Coupons.jsx";
import PartnerOnboarding from "./screens/Partners/PartnerOnboarding.jsx";

// REDUX IMPORTS
import { getActiveUser } from "./actions/authAction.js";
import OrderReport from "./screens/Operations/OrderReport.jsx";
import Sessions from "./screens/Partners/SessionList.jsx";
import Banners from "./screens/Growth/Banners.jsx";
import AgentDetails from "./screens/Partners/PartnerProfile.jsx";
import AgentServiceActions from "./screens/Partners/ServiceActions.jsx";
import BankInformationApprovalList from "./screens/Partners/BankApprovals.jsx";
import AddCouponModal from "./screens/Growth/CouponForm.jsx";
import AgentPayout from "./screens/Partners/Payouts.jsx";
import OrderFollowUp from "./screens/Operations/OrderFollowUp.jsx";
import TemplateList from "./screens/Admin/TemplateList.jsx";
import SendTemplate from "./screens/Admin/SendTemplate.jsx";
import BlogList from "./screens/Growth/BlogList.jsx";
import UpdateBlogDetails from "./screens/Growth/BlogEditor.jsx";
import AIChatList from "./screens/AIChat/AiChatList.jsx";
import Dashboard from "./screens/Dashboard/Dashboard.jsx";
import AppLayout from "./components/shell/AppLayout.jsx";
import OverlayProvider from "./components/overlays/OverlayProvider.jsx";
import { landingPathFor } from "./components/shell/navConfig.js";
import ViewAIChat from "./screens/AIChat/AiTranscript.jsx";


const RoleAccess = ({ roles = [] }) => {
  const activeUser = useSelector((state) => state.auth?.active_user);
  const activeUserRoles = activeUser?.data?.roles || [];
  if (!activeUser) {
    return;
  }
  const hasAccess = roles.some((role) => activeUserRoles.includes(role));
  return hasAccess ? <Outlet /> : <Navigate to="/unauthorized" replace />;
};


const App = () => {
  const [auth, setAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const user = sessionStorage.getItem('auth');
  

  const dispatch = useDispatch();
  const { store } = useContext(ReactReduxContext);
  const activeUserRoles = useSelector((state) => state.auth?.active_user?.data?.roles);
  const loadingState = useSelector((state) => state.agent?.loading);


  const fetchLoggedInUser = async () => {
    const active_user = store.getState()?.auth?.active_user;
    if (active_user === undefined) {
      const accessToken = JSON.parse(user)?.accessToken;
      await getActiveUser(dispatch, accessToken).then((res) => {}).catch((error) => {})
    }
  };


  useEffect(() => {
    if (loadingState !== undefined) setLoading(loadingState);
  }, [loadingState]);


  useEffect(() => {
    if (user && user !== 'null') {
      try {
        const accessToken = JSON.parse(user).hasOwnProperty("accessToken");
        if (accessToken) {
          setAuth(true);
        }
      } catch {
        sessionStorage.removeItem('auth');
        setAuth(false);
      }
    } else {
      sessionStorage.removeItem('auth');
    }
  }, [user]);


  useEffect(() => {
    if (auth) {
      fetchLoggedInUser();
    }
    // eslint-disable-next-line
  }, [auth]);

  
  return (
    <>
      {loading && (
        <div className="loader">
          <Loader />
        </div>
      )}
      <Router>
        <OverlayProvider>
        <Routes>
          {/* Landing route: send the user to the first screen their roles can reach.
              Previously this was a hand-maintained if-chain that silently fell through
              to the login form for roles nobody had added (blog, report, …). */}
          <Route
            path="/"
            element={
              activeUserRoles?.length ? (
                <Navigate to={landingPathFor(activeUserRoles)} replace />
              ) : (
                <Login />
              )
            }
          />
          {/* #prod add l1_reviewer, l2_reviewer nad change /invite-user -> /dashboard */}
          <Route
            path="/forgot-password"
            element={
              activeUserRoles?.length ? (
                <Navigate to={landingPathFor(activeUserRoles)} replace />
              ) : (
                <ForgotPassword />
              )
            }
          />
          {/* #prod add l1_reviewer, l2_reviewer nad change /invite-user -> /dashboard */}
          <Route
            path="/reset"
            element={
              activeUserRoles?.length ? (
                <Navigate to={landingPathFor(activeUserRoles)} replace />
              ) : (
                <ResetPassword />
              )
            }
          />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<NotFound />} />
          <Route
            element={<AppLayout />}
          >
            {/* #prod */}
            {/* <Route
              element={
                <RoleAccess roles={["super_admin", "admin", "l1_reviewer", "l2_reviewer"]} />
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/agent-detail/:id" element={<AgentDetails />} />
            </Route> */}
           
            <Route element={<RoleAccess roles={["super_admin", "admin"]} />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/invite-user" element={<CreateAdmin />} />
              <Route path="/admin-list" element={<AdminList />} />
              <Route path="/email-template" element={<TemplateList channel="email" />} />
              <Route path="/email-template/:id" element={<SendTemplate channel="email" />} />
              <Route path="/whatsapp-template" element={<TemplateList channel="whatsapp" />} />
              <Route path="/whatsapp-template/:id" element={<SendTemplate channel="whatsapp" />} />
              <Route path="/partner-payout" element={<AgentPayout />} />
              <Route path="/agent-bank-information" element={<BankInformationApprovalList />} />
              {/* <Route path="/approved-astrologers" element={<ApprovedAstrologer />} /> */}
            </Route>
           
            <Route
              element={<RoleAccess roles={["super_admin", "admin", "app_video"]} />}
            >
              <Route path="/app-video" element={<AppVideo />} />
            </Route>
              
            {/* #prod */}
            <Route
              element={<RoleAccess roles={["super_admin", "admin", "promotion"]} />}
            >
              <Route path="/banners" element={<Banners />} />
              
              {/* <Route path="/agent-promotion" element={<PromotionPricing />} />
              <Route path="/agent-promotion-edit/:id" element={<PromotionPricingEdit />} /> */}
              
              <Route path="/coupon-promotion" element={<CouponPromotions />} />
              <Route path="/coupon-promotion/:action" element={<AddCouponModal />} />
            </Route>
           
            <Route
              element={<RoleAccess roles={["super_admin",  "admin", "name_correction"]} />}
            >
              <Route path="/name-correction" element={<NameCorrection />} />
            </Route>
           
            {/* <Route
              element={<RoleAccess roles={["super_admin", "admin", "send_notification"]} />}
            >
              <Route path="/promotional-notification" element={<PromotionalNotification />} />
            </Route> */}
           
            <Route
              element={<RoleAccess roles={["super_admin", "admin", "support"]} />}
            >
              <Route path="/support-ticket" element={<SupportTicket />} />
              <Route path="/order-follow-up" element={<OrderFollowUp />} />
            </Route>

            <Route
              element={<RoleAccess roles={["super_admin", "admin", "report"]} />}
            >
              <Route path="/report" element={<Report />} />
            </Route>

            <Route
              element={<RoleAccess roles={["super_admin", "admin", "ai_astrologer"]} />}
            >
              <Route path="/ai-chat" element={<AIChatList />} />
              <Route path="/ai-chat/:id" element={<ViewAIChat />} />
            </Route>


            <Route
              element={<RoleAccess roles={["super_admin", "admin", "agent_onboarding"]} />}
            >
              <Route path="/agent-onboarding" element={<PartnerOnboarding />} />
              <Route path="/agent-onboarding/:id" element={<AgentDetails />} />
              <Route path="/agent-service-actions" element={<AgentServiceActions />} />
            </Route>


            <Route
              element={<RoleAccess roles={["super_admin", "admin", "order_report"]} />}
            >
              <Route path="/order-report" element={<OrderReport />} />
            </Route>


            <Route
              element={<RoleAccess roles={["super_admin", "admin", "sessions"]} />}
            >
              <Route path="/sessions" element={<Sessions />} />
            </Route>

             <Route element={<RoleAccess roles={["super_admin", "admin", 'blog']} />}>
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/add" element={<UpdateBlogDetails />} />
              <Route path="/blog/:id" element={<UpdateBlogDetails />} />
            </Route>


            <Route path="/profile" element={<Profile />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        </OverlayProvider>
        <ToastContainer />
      </Router>
    </>
  );
};


export default App;
